import { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { HostApplication } from '@/app/host-application';
import { ErrorBoundary, RouteErrorBoundary } from '@/app/error-boundary';
import { queryClient } from '@/lib/query-client';
import { installErrorTracker } from '@/lib/error-tracker';
import { installAnalytics } from '@/lib/analytics';
import { clearMainAppBrowserState } from '@/lib/panel-runtime';
import { isPanelHostname } from '@/lib/panel-navigation';
import { loadTelegramSdk } from '@/lib/telegram';
import '@fontsource-variable/geist/index.css';
import '@fontsource-variable/geist-mono/index.css';
import '@/styles/globals.css';
import '@/styles/app-next.css';

const hostname = window.location.hostname;
const isPanelHost = isPanelHostname(hostname);
const ReactQueryDevtools = import.meta.env.DEV && !isPanelHost
  ? lazy(() => import('@tanstack/react-query-devtools').then((module) => ({
      default: module.ReactQueryDevtools,
    })))
  : null;

if (import.meta.env.DEV && 'serviceWorker' in navigator) {
  void navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())));
}

installErrorTracker();
if (isPanelHost) clearMainAppBrowserState();
else installAnalytics();

async function bootstrap() {
  if (!isPanelHost) {
    await loadTelegramSdk().catch(() => {
      if (import.meta.env.DEV) console.warn('Telegram SDK is unavailable');
    });
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <RouteErrorBoundary>
              <HostApplication location={window.location} />
              <Toaster richColors closeButton position="bottom-right" />
            </RouteErrorBoundary>
          </BrowserRouter>
          {ReactQueryDevtools && (
            <Suspense>
              <ReactQueryDevtools initialIsOpen={false} />
            </Suspense>
          )}
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}

void bootstrap();
